import remoteSdk from 'src/platform-sdk/initialized-platform-sdk';
import { phanesAPIClient } from '../api/clients';
import { ComponentType } from '../enums/ProvisioningFieldTypes';
import { ProvisioningStatus } from '../enums/ProvisioningStatus';
import {
  IEntitlementInstances,
  IProductInstance,
  IProvisioningMetaData,
  IProvisioningQuestion,
} from '../interfaces/ICustomersLists';
import { IGenericTagProps } from '../interfaces/IProductGenericTag';
import {
  IProductTransformed,
  IProductListErrorResponse,
  IProductListSuccessResponse,
  IProductTransformedData,
  IProductObjectTransformed,
  IConvertedProvisioningMetadata,
  ICustomersResponseData,
  ICustomersResponseDataList,
  IPaginationInfo,
} from '../interfaces/IProductList';
import {
  Activated,
  CISCO_DUO_PRODUCT,
  TEST_CII_PRODUCT,
  External,
  InActive,
  Legacy,
  Provisioned,
  Trial,
  CISCO_IDENTITY_INTELLIGENCE,
  secure_access_SKUs,
  Hibernated,
  CISCO_SSE_PRODUCT,
  DUO_WITH_IDENTITY_INTELLIGENCE,
  FEATURE_FLAG_CONSTANTS,
  CISCO_DUO_SERVICE,
  CISCO_DUO,
  CISCO_MPE_PRODUCT,
  CISCO_SDWAN_SERVICE_DESCRIPTION,
  TEST_CISCO_SDWAN_SERVICE_DESCRIPTION,
  FIREWALL_PRODUCT,
  SCC_ENABLED_FIREWALL,
} from '../utils/constant';
import { hasSseAusEntitlement } from '../utils/productUtils';

let isMspManagerType = false;
let isMspManagedType = false;

// Local storage for pending products to avoid API calls on page changes
let cachedPendingProducts: IProductInstance[] = [];
let cachedEnterpriseId: string = '';
let cachedLimit: number = 0;

// Function to clear cached pending products data
export const clearCachedPendingProducts = (): void => {
  cachedPendingProducts = [];
  cachedEnterpriseId = '';
  cachedLimit = 0;
};

function getProvisioningQuestionsMetaData(
  provisioningQuestions: IProvisioningQuestion[],
  provisioningMetadata: IProvisioningMetaData[]
): IConvertedProvisioningMetadata[] {
  if (provisioningQuestions.length > 0) {
    return provisioningQuestions.map((provisioningQuestion) => {
      let questionValue = '';
      if (
        provisioningQuestion.type === ComponentType.DROP_DOWN &&
        provisioningQuestion.options.length === 1
      ) {
        questionValue = provisioningQuestion.options[0].value;
      }
      return {
        name: provisioningQuestion.name,
        value: questionValue,
      };
    });
  } else {
    return provisioningMetadata;
  }
}

export const getProductFlags = (
  product: IProductInstance,
  isProductDeleted: boolean = false,
  isOrgTrial: boolean = false,
  isHibernationEnabled: boolean = false
): IGenericTagProps[] => {
  const productTags: IGenericTagProps[] = [];

  const conditionsAndTags = [
    {
      condition: product.is_trial || isOrgTrial,
      tag: { ...Trial, noBackground: false },
    },
    { condition: product.isLegacy, tag: { ...Legacy, noBackground: false } },
    {
      condition: !product.isActive || isProductDeleted,
      tag: { ...InActive, noBackground: false },
    },
    {
      condition:
        product.isActive &&
        !isProductDeleted &&
        ![ProvisioningStatus.IN_PROGRESS, ProvisioningStatus.ERROR].includes(
          product.provisioning_status as ProvisioningStatus
        ) &&
        !(
          isHibernationEnabled &&
          [ProvisioningStatus.HIBERNATED].includes(
            product.provisioning_status as ProvisioningStatus
          )
        ) &&
        !(product.isExternal || product.isLegacy),
      tag: { ...Activated, noBackground: false },
    },
    {
      condition:
        product.isActive &&
        !isProductDeleted &&
        [ProvisioningStatus.IN_PROGRESS, ProvisioningStatus.ERROR].includes(
          product.provisioning_status as ProvisioningStatus
        ),
      tag: { ...Provisioned, noBackground: false },
    },
    {
      condition: product.isExternal,
      tag: { ...External, noBackground: false },
    },
    {
      condition:
        isHibernationEnabled &&
        [ProvisioningStatus.HIBERNATED].includes(
          product.provisioning_status as ProvisioningStatus
        ),
      tag: { ...Hibernated, noBackground: false },
    },
  ];
  conditionsAndTags.forEach(({ condition, tag }) => {
    if (condition) {
      productTags.push(tag);
    }
  });
  return productTags;
};

/**
 * Transforms CII and Duo Premier entitlement descriptions to unified Duo messaging
 * when the feature flag is enabled.
 * @param ciiEntitlement - CII entitlement instance to transform
 * @param duoPremier - Duo Premier entitlement instance to transform
 * @param isDuoMessagingEnabled - Feature flag indicating if Duo messaging should be enabled
 */
function transformDuoEntitlementDescriptions(
  ciiEntitlement: IEntitlementInstances | undefined,
  duoPremier: IEntitlementInstances | undefined,
  isDuoMessagingEnabled: boolean
): void {
  if (!isDuoMessagingEnabled || !duoPremier?.entitlement.is_service) {
    return;
  }

  if (ciiEntitlement?.entitlement?.description) {
    ciiEntitlement.entitlement.description = DUO_WITH_IDENTITY_INTELLIGENCE;
  }

  if (duoPremier?.entitlement?.description) {
    duoPremier.entitlement.description = DUO_WITH_IDENTITY_INTELLIGENCE;
  }
}

function getProductObject(
  product: IProductInstance,
  isHibernationEnabled: boolean
): IProductObjectTransformed {
  return {
    productId: product?.ID,
    activationCode: product?.activationCode,
    instanceId: product?.external_tenant_id,
    name: product?.product.name,
    rawProductName: product?.product.name,
    isProductComposite: product.product.is_composite ?? false,
    provisionStatus: product?.provisioning_status,
    region: product?.region,
    isTrial: product?.is_trial ?? false,
    lastAutoRevertedAt: product?.lastAutoRevertedAt ?? '',
    hibernatedSubExternalId: product?.hibernatedSubExternalId ?? '',
    isActive: product?.isActive ?? true,
    isHidden: product?.isHidden ?? false,
    isLegacy: product?.isLegacy ?? false,
    isUseExistingTenantSet: product?.is_use_existing_tenant_set ?? false,
    isEA: product?.isEA ?? false,
    productAllowedRegions: product?.product?.product_allowed_regions,
    initialAdmin: product?.initialAdmin ?? '',
    productTags: getProductFlags(product, false, false, isHibernationEnabled),
    provisioningQuestionsMetaData: getProvisioningQuestionsMetaData(
      product?.product?.provisioning_questions ?? [],
      product?.product?.provisioning_metadata ?? []
    ),
    additionalMetadata: product?.additionalTenantMetadata ?? [],
    provisioningQuestions: product?.product?.provisioning_questions ?? [],
    isOrgEntitledProductCard: Object.hasOwn(
      product,
      'org_entitlement_instances'
    ),
    isSseAusEntitlement: hasSseAusEntitlement(product),
  };
}

/**
 * Filters and returns valid entitlements based on the following requirements:
 *
 * 1. When both CII and DUO exist → Only DUO returns
 * 2. When only CII exists → CII returns
 * 3. Other entitlements with description should return
 * 4. Filters out revoked subscriptions (for MSP)
 * 5. Services return when:
 *    - Feature flag exists AND is_use_existing_tenant_set is true, OR
 *    - They are the only products available (status not FINISHED) with description and not revoked
 *
 * @param product - Product instance containing entitlements
 * @param unifiedOnboardingIIFeatureFlagExist - Feature flag for unified onboarding II
 * @param isSubscriptionListView - Whether this is subscription list view
 * @param isMspManager - Whether the user is an MSP manager
 * @returns Filtered array of entitlement instances
 */
function getEntitlements(
  product: IProductInstance,
  unifiedOnboardingIIFeatureFlagExist: boolean = false,
  isSubscriptionListView: boolean = false,
  isMspManager: boolean = false
): IEntitlementInstances[] {
  let productEntitlements =
    product?.org_entitlement_instances ?? product.entitlement_instances;

  if (product.provisioning_status !== ProvisioningStatus.FINISHED) {
    productEntitlements = productEntitlements.filter((productEntitlement) => {
      return !productEntitlement?.Deleted;
    });
    const tempProductEntitlements = productEntitlements.filter(
      (productEntitlement) => {
        return !productEntitlement?.entitlement?.is_service;
      }
    );
    if (tempProductEntitlements.length > 0) {
      productEntitlements = Array.from(tempProductEntitlements);
    }
  }

  // Check if we have only service products (happens when status is not FINISHED and no base products exist)
  const hasOnlyServices = productEntitlements.every(
    (ent) => ent.entitlement?.is_service === true
  );

  let orgEntitlementInstances = productEntitlements.filter(
    (productEntitlement) => {
      const isRevoked = productEntitlement?.subscription?.revoked === true; // Required for MSP
      const hasDescription =
        productEntitlement?.entitlement?.description !== '';
      const isAddOn = productEntitlement?.isAddOn === true;
      const isService = productEntitlement.entitlement?.is_service === true;
      const isBaseProduct = !isAddOn && !isService;

      // Base products must have all three conditions
      const isValidBaseProduct = isBaseProduct && !isRevoked && hasDescription;

      // Special case: MPE service products can have empty description
      const isMPEService =
        isService && product.product?.name === CISCO_MPE_PRODUCT;

      // Services can pass through if:
      // 1. Feature flag is enabled AND product uses existing tenant, OR
      // 2. They are the only products available (hasOnlyServices) and have description, OR
      // 3. It's an MPE service product with IN_PROGRESS status
      const isValidService =
        isService &&
        (isMPEService ||
          (unifiedOnboardingIIFeatureFlagExist &&
            product.is_use_existing_tenant_set) ||
          (hasOnlyServices && hasDescription && !isRevoked));

      return isValidBaseProduct || isValidService;
    }
  );

  const ciiEntitlement = orgEntitlementInstances.find(
    (ent) =>
      ent.entitlement?.description?.toLowerCase() ===
        CISCO_IDENTITY_INTELLIGENCE && Boolean(!ent.Deleted)
  );
  const duoPremier = orgEntitlementInstances.find(
    (ent) =>
      ent.entitlement?.description?.toLowerCase() === CISCO_DUO ||
      ent.entitlement?.description?.toLowerCase() === CISCO_DUO_SERVICE
  );

  const isDuoMessagingEnabled: boolean = remoteSdk.featureFlags.isEnabled(
    FEATURE_FLAG_CONSTANTS.ENABLE_DISPLAY_DUO_MESSAGING
  );
  transformDuoEntitlementDescriptions(
    ciiEntitlement,
    duoPremier,
    isDuoMessagingEnabled
  );
  if (ciiEntitlement === undefined || ciiEntitlement === null) {
    orgEntitlementInstances = orgEntitlementInstances.filter((entitlement) => {
      return (
        entitlement.entitlement.description?.toLowerCase() !==
        CISCO_IDENTITY_INTELLIGENCE
      );
    });
  }
  if (ciiEntitlement && duoPremier) return [duoPremier];
  if (
    Object.hasOwn(product, 'org_entitlement_instances') &&
    orgEntitlementInstances.length >= 0
  ) {
    return orgEntitlementInstances;
  } else if (
    isSubscriptionListView &&
    (isMspManager ||
      (isMspManagedType &&
        product.product.name === CISCO_SSE_PRODUCT &&
        isSharedSubscription(productEntitlements)))
  ) {
    return productEntitlements;
  } else {
    return productEntitlements.length > 0 ? [productEntitlements[0]] : [];
  }
}

/**
 * Checks whether all entitlement instances belong to the same subscription,
 * and verifies that the subscription is shared from a manager
 * and is not revoked.
 */
function isSharedSubscription(
  entitlementInstances: IEntitlementInstances[]
): boolean {
  if (!entitlementInstances || entitlementInstances.length === 0) return false;

  const firstSub = entitlementInstances[0]?.subscription;
  if (
    !firstSub?.externalId ||
    !firstSub.manager_subscription_id ||
    firstSub.revoked === true
  )
    return false;

  return entitlementInstances.every(
    (ent) => ent?.subscription?.externalId === firstSub.externalId
  );
}

function getProductName(
  productObject: IProductObjectTransformed,
  iEntitlementInstance: IEntitlementInstances,
  subUiFeatureFlagExist: boolean = false
): string {
  if (
    // For MSP Manager and MSP Managed with shared subscription only
    (isMspManagerType ||
      (isMspManagedType &&
        iEntitlementInstance?.subscription?.manager_subscription_id &&
        !iEntitlementInstance?.subscription?.revoked)) &&
    iEntitlementInstance?.entitlement?.description === ''
  ) {
    // Getting the secure access product description
    const newDescription = secure_access_SKUs.get(
      iEntitlementInstance?.entitlement?.value
    );
    if (newDescription) {
      iEntitlementInstance.entitlement.description = newDescription;
    }
  }

  let productName: string;
  if (
    subUiFeatureFlagExist &&
    iEntitlementInstance?.entitlement?.description &&
    !iEntitlementInstance?.isAddOn
  ) {
    productName = productObject.isOrgEntitledProductCard
      ? iEntitlementInstance?.entitlement?.description
      : `${productObject?.name} ${iEntitlementInstance?.entitlement?.description}`;
  } else {
    productName = productObject?.name;
  }

  return productName;
}

function getDifferentTilesData(
  productEntitlements: IEntitlementInstances[],
  productObject: IProductObjectTransformed,
  product: IProductInstance,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false,
  unifiedOnboardingIIFeatureFlagExist: boolean = false
): IProductTransformed[] {
  const finalProductObject: IProductTransformed[] = [];
  for (const productEntitlement of productEntitlements) {
    const isProductDeleted = Boolean(productEntitlement.Deleted);
    const isOrgTrial = Boolean(productEntitlement.is_trial);
    finalProductObject.push({
      ...productObject,
      ...getSubscriptionData(
        productEntitlement,
        productObject,
        subUiFeatureFlagExist,
        unifiedOnboardingFeatureFlagExist,
        unifiedOnboardingIIFeatureFlagExist
      ),
      productTags:
        isProductDeleted || isOrgTrial
          ? getProductFlags(product, isProductDeleted, isOrgTrial)
          : productObject.productTags,
    });
  }
  return finalProductObject;
}
/* pending products */
function getSubscriptionData(
  productEntitlement: IEntitlementInstances,
  productObject: IProductObjectTransformed,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false,
  unifiedOnboardingIIFeatureFlagExist: boolean = false
): IProductTransformed {
  const subscriptionData =
    productObject?.isTrial || productEntitlement?.is_trial
      ? productEntitlement?.trial
      : productEntitlement?.subscription;
  return {
    ...productObject,
    name: getProductName(
      productObject,
      productEntitlement,
      subUiFeatureFlagExist
    ),
    isAddOn: productObject?.isAddOn ?? false,
    isProductComposite: productObject?.isProductComposite ?? false,
    units: productEntitlement?.units ?? '',
    isTrial: productObject?.isTrial || productEntitlement?.is_trial,
    lastAutoRevertedAt: productObject.lastAutoRevertedAt
      ? productObject.lastAutoRevertedAt
      : productEntitlement.lastAutoRevertedAt,
    hibernatedSubExternalId: productObject.hibernatedSubExternalId
      ? productObject.hibernatedSubExternalId
      : productEntitlement.hibernatedSubExternalId,
    isService: productEntitlement?.entitlement?.is_service ?? false,
    seat: productEntitlement?.quantity ?? '-',
    totalSeat: productEntitlement?.msp_total_quantity ?? '-',
    availableSeat: productEntitlement?.msp_available_quantity ?? '-',
    managerSubscriptionId: subscriptionData?.manager_subscription_id ?? '',
    managerOrgName: subscriptionData?.msp_manager_org_details?.name ?? '',
    operand: productEntitlement?.operand ?? '-',
    operation: productEntitlement?.operation ?? '-',
    tier: productEntitlement?.entitlement?.value ?? '',
    productDescription: productEntitlement?.entitlement?.description || '',
    subscriptionId: subscriptionData?.externalId || '-',
    startDate:
      (productObject?.isTrial || productEntitlement?.is_trial
        ? subscriptionData?.trial_start_date
        : subscriptionData?.requested_start_date) ?? '',
    expiryDate:
      (productObject?.isTrial || productEntitlement?.is_trial
        ? subscriptionData?.trial_end_date
        : subscriptionData?.subscription_end_date) ?? '',
    isExternal: productEntitlement?.sku?.is_external ?? false,
    productName: subscriptionData?.product_name ?? '',
    canProvision: subscriptionData?.is_provisionable ?? false,
    revoked: subscriptionData?.revoked ?? false,
    licenseType: productEntitlement?.license_type ?? '',
    endCustomerName: subscriptionData?.endCustomerName?.[0] ?? '',
    productSubscriptionId: subscriptionData.ID ?? '',
    entitlementInstanceId: productEntitlement?.ID ?? '',
    assignedOrganizationsCount: productEntitlement?.assigned_managed_orgs_count,
    isPlatformSubscription: subscriptionData?.is_platform_subscription ?? false,
  };
}

function getFinalProductObject(
  product: IProductInstance,
  productEntitlements: IEntitlementInstances[],
  productObj: IProductObjectTransformed,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false,
  unifiedOnboardingIIFeatureFlagExist: boolean = false,
  isSubscriptionListView: boolean = false,
  isMspManager: boolean = false
): IProductTransformed[] {
  let finalProductObject: IProductTransformed[] = [];

  const entitlements =
    product.entitlement_instances ?? product.org_entitlement_instances ?? [];

  // Find all add-on entitlements
  const addOnEntitlements = entitlements
    .filter((ent) => ent.isAddOn === true)
    .map((ent) => {
      return {
        ...ent,
        productTags:
          ent.Deleted != null ? [{ ...InActive, noBackground: false }] : [],
      };
    });

  if (
    subUiFeatureFlagExist &&
    productObj?.isOrgEntitledProductCard &&
    productEntitlements.length >= 1 &&
    product?.provisioning_status === 'FINISHED'
  ) {
    finalProductObject = getDifferentTilesData(
      productEntitlements,
      productObj,
      product,
      subUiFeatureFlagExist,
      unifiedOnboardingFeatureFlagExist,
      unifiedOnboardingIIFeatureFlagExist
    ).map((obj) => ({
      ...obj,
      subRows: addOnEntitlements,
    }));
  } else if (
    isSubscriptionListView &&
    (isMspManager ||
      (isMspManagedType && productObj.name === CISCO_SSE_PRODUCT))
  ) {
    productEntitlements.forEach((entitlement) => {
      const obj: IProductTransformed = {
        ...productObj,
        ...getSubscriptionData(
          entitlement,
          productObj,
          subUiFeatureFlagExist,
          unifiedOnboardingFeatureFlagExist,
          unifiedOnboardingIIFeatureFlagExist
        ),
        subRows: addOnEntitlements,
      };
      finalProductObject.push(obj);
    });
  } else {
    const obj: IProductTransformed = {
      ...productObj,
      ...getSubscriptionData(
        productEntitlements[0],
        productObj,
        subUiFeatureFlagExist,
        unifiedOnboardingFeatureFlagExist,
        unifiedOnboardingIIFeatureFlagExist
      ),
      subRows: addOnEntitlements,
    };
    finalProductObject.push(obj);
  }

  return finalProductObject;
}

function getKeyToPush(
  unifiedOnboardingFeatureFlagExist: boolean,
  productObject: IProductObjectTransformed
): 'otherProducts' | 'pendingProducts' {
  if (unifiedOnboardingFeatureFlagExist) {
    return productObject?.provisionStatus === ProvisioningStatus.FINISHED ||
      productObject?.provisionStatus === ProvisioningStatus.HIBERNATED
      ? 'otherProducts'
      : 'pendingProducts';
  } else {
    return productObject?.provisionStatus === ProvisioningStatus.NEW ||
      productObject?.provisionStatus === ProvisioningStatus.ACTION_REQUIRED
      ? 'pendingProducts'
      : 'otherProducts';
  }
}

function filterResetInactiveTenants(
  products: IProductInstance[]
): IProductInstance[] {
  return products.filter((productData: any) => {
    const productEntitlements = (
      productData?.entitlement_instances || []
    ).filter((productEntitlement: any) => {
      return !productEntitlement?.Deleted;
    });
    return !(
      productData.provisioning_status === ProvisioningStatus.NEW &&
      productData.external_tenant_id === '' &&
      productEntitlements.length === 0
    );
  });
}

function checkHasPaidFirewall(productsData: IProductInstance[]): boolean {
  return productsData.some((product) => {
    if (product.product?.name !== FIREWALL_PRODUCT) {
      return false;
    }

    // Only check finished products
    if (product.provisioning_status !== ProvisioningStatus.FINISHED) {
      return false;
    }

    const orgEntitlements = product.org_entitlement_instances || [];
    return orgEntitlements.some((entitlement) => {
      const isActive = entitlement.Deleted === null;
      const isNonService = entitlement.entitlement?.is_service === false;
      const skuName = entitlement.sku?.Name;
      const isNotUiEnabled =
        skuName && skuName !== '' && skuName !== SCC_ENABLED_FIREWALL;

      return isActive && isNonService && isNotUiEnabled;
    });
  });
}

export const fetchProductList = async (
  enterpriseId: string,
  unifiedOnboardingFeatureFlagExist: boolean = false,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingIIFeatureFlagExist: boolean = false,
  isSubscriptionListView: boolean = false,
  isMspManager: boolean = false,
  isHibernationEnabled: boolean = false,
  isMspManaged: boolean = false,
  isNextGenSase: boolean = false,
  isPaginationEnabled: boolean = false,
  limit: number = 10,
  offset: number = 0,
  isPageChange: boolean = false
): Promise<IProductListSuccessResponse | IProductListErrorResponse> => {
  isMspManagerType = isMspManager; // Update the global variable to reflect the current state
  isMspManagedType = isMspManaged;

  try {
    let requestUrl = `/v1/customers?enterprise_id=${enterpriseId}`;
    if (isPaginationEnabled && isMspManager) {
      requestUrl = `/v1/customers?enterprise_id=${enterpriseId}&limit=${limit}&offset=${offset}&provisioning_successful=true`;
    }
    const response: ICustomersResponseData | ICustomersResponseDataList =
      await phanesAPIClient.get(requestUrl);
    let pagination: IPaginationInfo | null = null;

    let productsData: IProductInstance[] = [];
    if ('productInstances' in response.data) {
      productsData = response.data.productInstances;
    } else {
      productsData = response.data as unknown as IProductInstance[];
    }
    if ('pagination' in response.data && response.data.pagination) {
      const {
        offset = 0,
        limit = 0,
        totalRecords = 0,
      } = response.data.pagination as Partial<IPaginationInfo>;
      pagination = { offset, limit, totalRecords };
    }

    // Additional API call for pending products when pagination is enabled for MSP Manager
    let pendingProductsData: IProductInstance[] = [];
    if (isPaginationEnabled && isMspManager) {
      if (
        isPageChange &&
        cachedEnterpriseId === enterpriseId &&
        cachedLimit === limit
      ) {
        pendingProductsData = cachedPendingProducts;
      } else {
        if (cachedEnterpriseId !== enterpriseId || cachedLimit !== limit) {
          clearCachedPendingProducts();
        }

        // Only make API call for initial page load or when parameters change
        const pendingRequestUrl = `/v1/customers?enterprise_id=${enterpriseId}&provisioning_successful=false`;
        const pendingResponse:
          | ICustomersResponseData
          | ICustomersResponseDataList =
          await phanesAPIClient.get(pendingRequestUrl);

        if ('productInstances' in pendingResponse.data) {
          pendingProductsData = pendingResponse.data.productInstances;
        } else {
          pendingProductsData =
            pendingResponse.data as unknown as IProductInstance[];
        }

        // Cache the pending products data for future page changes
        cachedPendingProducts = pendingProductsData;
        cachedEnterpriseId = enterpriseId;
        cachedLimit = limit;
      }
    }

    // Check if there's an active MPE in the raw response (before filtering)
    const hasActiveMPE = productsData.some(
      (product) =>
        product.product?.name === CISCO_MPE_PRODUCT && product.isActive === true
    );

    // Check if there's a paid firewall subscription (not UI-enabled)
    const hasPaidFirewall = checkHasPaidFirewall(productsData);

    let productTransformedData: IProductTransformedData = {
      allProducts: [],
      pendingProducts: [],
      otherProducts: [],
      hasActiveMPE,
      hasPaidFirewall,
    };

    let nonHiddenProducts = productsData.filter((productData) => {
      return (
        Object.hasOwn(productData, 'org_entitlement_instances') ||
        !productData.isHidden
      );
    });

    nonHiddenProducts = filterResetInactiveTenants(nonHiddenProducts);

    if (nonHiddenProducts.length > 0) {
      productTransformedData = nonHiddenProducts.reduce(
        (finalData: IProductTransformedData, product) => {
          const productObject: IProductObjectTransformed = getProductObject(
            product,
            isHibernationEnabled
          );
          const productEntitlements = getEntitlements(
            product,
            unifiedOnboardingIIFeatureFlagExist,
            isSubscriptionListView,
            isMspManager
          );

          if (productEntitlements.length <= 0) {
            return finalData;
          }
          const finalProductObject = getFinalProductObject(
            product,
            productEntitlements,
            productObject,
            subUiFeatureFlagExist,
            unifiedOnboardingFeatureFlagExist,
            unifiedOnboardingIIFeatureFlagExist,
            isSubscriptionListView,
            isMspManager
          );

          finalData[
            getKeyToPush(unifiedOnboardingFeatureFlagExist, productObject)
          ].push(...finalProductObject);
          finalData.allProducts.push(...finalProductObject);
          return finalData;
        },
        {
          allProducts: [],
          pendingProducts: [],
          otherProducts: [],
          hasActiveMPE,
          hasPaidFirewall,
        }
      );
    }
    function filterPendingProducts(
      products: IProductTransformed[]
    ): IProductTransformed[] {
      return products.filter(
        (product) =>
          !product?.isService ||
          (product?.isService &&
            (product?.rawProductName === CISCO_DUO_PRODUCT ||
              product?.rawProductName === CISCO_MPE_PRODUCT ||
              product?.rawProductName === TEST_CII_PRODUCT ||
              ((product?.rawProductName === CISCO_SDWAN_SERVICE_DESCRIPTION ||
                product?.rawProductName ===
                  TEST_CISCO_SDWAN_SERVICE_DESCRIPTION) &&
                isNextGenSase)))
      );
    }

    productTransformedData.pendingProducts = filterPendingProducts(
      productTransformedData.pendingProducts
    );

    // Process additional pending products data for MSP Manager with pagination
    if (isPaginationEnabled && isMspManager && pendingProductsData.length > 0) {
      let nonHiddenPendingProducts = pendingProductsData.filter(
        (productData) => {
          return (
            Object.hasOwn(productData, 'org_entitlement_instances') ||
            !productData.isHidden
          );
        }
      );

      nonHiddenPendingProducts = filterResetInactiveTenants(
        nonHiddenPendingProducts
      );

      if (nonHiddenPendingProducts.length > 0) {
        const pendingTransformedData = nonHiddenPendingProducts.reduce(
          (finalData: IProductTransformed[], product) => {
            const productObject: IProductObjectTransformed = getProductObject(
              product,
              isHibernationEnabled
            );
            const productEntitlements = getEntitlements(
              product,
              unifiedOnboardingIIFeatureFlagExist,
              isSubscriptionListView,
              isMspManager
            );

            if (productEntitlements.length <= 0) {
              return finalData;
            }
            const finalProductObject = getFinalProductObject(
              product,
              productEntitlements,
              productObject,
              subUiFeatureFlagExist,
              unifiedOnboardingFeatureFlagExist,
              unifiedOnboardingIIFeatureFlagExist,
              isSubscriptionListView,
              isMspManager
            );

            finalData.push(...finalProductObject);
            return finalData;
          },
          []
        );

        productTransformedData.pendingProducts = filterPendingProducts(
          pendingTransformedData
        );
      }
    }

    productTransformedData.otherProducts =
      productTransformedData.otherProducts.filter((product) => {
        return (
          !(product.isService && product.provisionStatus === 'FINISHED') ||
          (product.isService &&
            isNextGenSase &&
            (product?.rawProductName === CISCO_SDWAN_SERVICE_DESCRIPTION ||
              product?.rawProductName ===
                TEST_CISCO_SDWAN_SERVICE_DESCRIPTION) &&
            product.provisionStatus === 'FINISHED')
        );
      });

    return {
      isOk: true,
      data: productTransformedData,
      compositeProductsDataList:
        'compositeProductInstancesInfo' in response.data
          ? response.data.compositeProductInstancesInfo
          : [],
      pagination,
      error: null,
    };
  } catch (error) {
    return {
      isOk: false,
      data: null,
      compositeProductsDataList: [],
      pagination: null,
      error,
    };
  }
};
